import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { api, call } from "../lib/api.ts";
import { ago, displayTz, fmtDate, fmtInt } from "../lib/format.ts";
import { useCan, useCity, useSession } from "../lib/session.tsx";
import { Icon, type IconName } from "../components/Icon.tsx";
import { PageHead } from "../components/Shell.tsx";
import { ResendWelcomeDialog } from "../components/ResendWelcome.tsx";
import { ConfirmDialog, Dialog, ErrorBox, Initials, Skeleton, Stat, Switch, useToast } from "../components/ui.tsx";
import { typeLabel } from "../lib/trip-types.ts";

const DOCS: Record<string, { label: string; icon: IconName }> = {
  cnh_front: { label: "CNH · frente", icon: "idcard" },
  cnh_back: { label: "CNH · verso", icon: "idcard" },
  crlv: { label: "CRLV-e", icon: "doc" },
  background_state: { label: "Antecedentes · estadual", icon: "shield" },
  background_federal: { label: "Antecedentes · federal", icon: "shield" },
  profile_photo: { label: "Foto de perfil", icon: "camera" },
};
const DOC_STATUS: Record<string, { label: string; tone: string }> = {
  pending: { label: "A ANALISAR", tone: "var(--warn)" },
  approved: { label: "OK", tone: "var(--ok)" },
  rejected: { label: "REENVIAR", tone: "var(--bad)" },
  expired: { label: "VENCIDO", tone: "var(--bad)" },
};
const REVIEW_STATUS: Record<string, { label: string; tone: string }> = {
  pending_review: { label: "Novo", tone: "tone-brasa" },
  resubmission_requested: { label: "Reenvio", tone: "tone-warn" },
  approved: { label: "Aprovado", tone: "tone-ok" },
  rejected: { label: "Reprovado", tone: "tone-bad" },
  suspended: { label: "Suspenso", tone: "tone-bad" },
  draft: { label: "Rascunho", tone: "tone-neutral" },
};
const CHECKS: Record<string, string> = { cnh_valid: "CNH válida", cnh_ear: "CNH com EAR", documents_complete: "Documentos completos", vehicle: "Veículo cadastrado" };

type Queue = "pending" | "resubmission" | "documents" | "expiring" | "liveness";
type ExpiringItem = { kind: string; expiresAt: string };

const docName = (kind: string) => (kind === "cnh" ? "CNH" : (DOCS[kind]?.label ?? kind));
/** Today (YYYY-MM-DD) in the selected city. */
const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: displayTz() });
/** "CRLV-e venceu 12 out." / "CNH vence 3 nov. · +1 documento". */
function expiringText(items: ExpiringItem[]) {
  const first = items[0];
  if (!first) return "documento vencendo";
  const verb = first.expiresAt < today() ? "venceu" : "vence";
  // Date-only values: read at noon UTC so the city's day does not shift back.
  return `${docName(first.kind)} ${verb} ${fmtDate(`${first.expiresAt}T12:00:00Z`)}${items.length > 1 ? ` · +${items.length - 1} documento${items.length > 2 ? "s" : ""}` : ""}`;
}

export function DriversPage() {
  const { allCities } = useSession();
  const { cityId } = useCity();
  const { fila, id } = useSearch({ from: "/app/cadastros" });
  const navigate = useNavigate({ from: "/cadastros" });
  const list = useQuery({
    queryKey: ["drivers", cityId, fila],
    queryFn: () => call(api.admin.drivers.get({ query: { cityId, queue: fila } })),
    refetchInterval: 60_000,
  });
  const selected = id ?? list.data?.items[0]?.userId;
  const tabs: [Queue, string, number | undefined][] = [
    ["pending", "Aguardando", list.data?.counts.pending],
    ["resubmission", "Reenvio", list.data?.counts.resubmission],
    ["documents", "Documentos", list.data?.counts.documents],
    ["expiring", "Docs vencendo", list.data?.counts.expiring],
    ["liveness", "Prova de vida", list.data?.counts.liveness],
  ];

  return (
    <>
      <PageHead icon="user" title="Cadastros de motoristas">
        <div role="tablist" aria-label="Fila" className="segmented">
          {tabs.map(([q, label, n]) => (
            <button key={q} type="button" role="tab" aria-selected={fila === q} onClick={() => navigate({ search: { fila: q, id: undefined } })}>
              {label}{n !== undefined ? ` · ${fmtInt(n)}` : ""}
            </button>
          ))}
        </div>
        {allCities && <Link to="/cadastros/site" className="btn" style={{ textDecoration: "none" }}><Icon name="idcard" size={16} />Cadastros pelo site</Link>}
      </PageHead>

      {fila !== "liveness" && fila !== "documents" && <section aria-label="Resumo da fila" className="grid-auto">
        <Stat label="Tempo médio de análise" value={list.data?.week.avgReviewHours != null ? `${Math.round(list.data.week.avgReviewHours)} h` : "—"}>
          <span className="muted" style={{ fontSize: 12 }}>meta 48 h · últimos 7 dias</span>
        </Stat>
        <Stat label="Aprovados esta semana" value={fmtInt(list.data?.week.approved ?? 0)} />
        <Stat label="Mais antigo na fila" value={fila !== "expiring" && list.data?.items[0] ? ago(list.data.items[0].submittedAt) : "—"}>
          <span className="muted" style={{ fontSize: 12 }}>Análise manual (fase 1)</span>
        </Stat>
      </section>}
      {fila === "liveness" && (
        <p className="muted" style={{ margin: 0 }}>Confira o vídeo com a foto da CNH antes de liberar o GO. O motorista fica offline até a decisão.</p>
      )}
      {fila === "documents" && (
        <p className="muted" style={{ margin: 0 }}>Documentos enviados por motoristas já aprovados: renovações feitas pelo app e reenvios pedidos pela equipe. Aprove ou peça de novo, um a um.</p>
      )}

      <ErrorBox error={list.error} />
      <div className="row">
        <section aria-label="Fila de cadastros" className="card" style={{ flex: "1 1 290px", minWidth: 0, padding: 8, display: "flex", flexDirection: "column", gap: 2 }}>
          {list.isPending && [0, 1, 2].map((i) => <div key={i} style={{ padding: 12 }}><Skeleton h={40} /></div>)}
          {list.data?.items.length === 0 && <div className="empty">{fila === "liveness" ? "Nenhum vídeo aguardando." : fila === "documents" ? "Nenhum documento aguardando." : "Fila vazia. Bom trabalho."}</div>}
          {list.data?.items.map((d) => {
            const st = REVIEW_STATUS[d.status] ?? REVIEW_STATUS.draft!;
            const on = d.userId === selected;
            return (
              <button
                key={d.userId}
                type="button"
                aria-pressed={on}
                onClick={() => navigate({ search: { fila, id: d.userId } })}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 14, textAlign: "left", border: 0, background: on ? "var(--chip)" : "transparent" }}
              >
                <Initials text={d.initials} photo={d.photoUrl} size={40} />
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  <b>{d.name}</b>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {d.vehicle ?? "sem veículo"} · {fila === "expiring" ? expiringText(d.expiring) : ago(d.submittedAt)}
                  </span>
                  {fila === "expiring" && d.renewalRequestedAt && <span className="muted" style={{ fontSize: 11 }}>Renovação pedida {ago(d.renewalRequestedAt)}</span>}
                </span>
                <span className={`pill ${fila === "liveness" || fila === "documents" ? "tone-brasa" : st.tone}`} style={{ fontSize: 11, fontWeight: 700 }}>{fila === "liveness" ? "Vídeo" : fila === "documents" ? "Documento" : st.label}</span>
              </button>
            );
          })}
        </section>
        <section aria-label="Detalhe do cadastro" className="card" style={{ flex: "2.4 1 520px", minWidth: 0, padding: 22 }}>
          {selected ? <DriverDetail key={selected} driverId={selected} expiring={fila === "expiring" ? list.data?.items.find((i) => i.userId === selected) : undefined} /> : <div className="empty">Selecione um cadastro.</div>}
        </section>
      </div>
    </>
  );
}

function DriverDetail({ driverId, expiring }: { driverId: string; expiring?: { expiring: ExpiringItem[]; renewalRequestedAt: string | null } }) {
  const qc = useQueryClient();
  const canDecide = useCan("motoristas", "aprovar");
  const canEdit = useCan("motoristas", "editar");
  const [renewing, setRenewing] = useState(false);
  const [welcoming, setWelcoming] = useState(false);
  const toast = useToast();
  const [dialog, setDialog] = useState<null | "approve" | "resubmit" | "reject">(null);
  /** The document open in the viewer (where it is decided). */
  const [viewing, setViewing] = useState<string | null>(null);
  const [pii, setPii] = useState<Awaited<ReturnType<typeof reveal>> | null>(null);
  const d = useQuery({ queryKey: ["driver", driverId], queryFn: () => call(api.admin.drivers({ id: driverId }).get()) });
  const reveal = () => call(api.admin.drivers({ id: driverId }).reveal.post());

  if (d.isPending) return <Skeleton h={300} />;
  if (d.error || !d.data) return <ErrorBox error={d.error} />;
  const p = d.data;
  const undecided = p.reviewStatus === "pending_review" || p.reviewStatus === "resubmission_requested";
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["drivers"] });
    void qc.invalidateQueries({ queryKey: ["driver", driverId] });
  };
  const done = (msg: string) => {
    setDialog(null);
    toast.show(msg);
    refresh();
  };
  const isApproved = (kind: string) => p.documents.some((x) => x.kind === kind && x.status === "approved");
  const toApprove = p.requiredKinds.filter((k) => !isApproved(k));
  const toResend = p.documents.filter((x) => x.status === "rejected" || x.status === "expired");
  const waiting = p.documents.filter((x) => x.status === "pending");

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        <Initials text={p.initials} photo={p.photoUrl} className="avatar-lg" />
        <span style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 2 }}>
          <b className="display" style={{ fontSize: 21 }}>{p.fullName}</b>
          <span style={{ fontSize: 14, color: "var(--text-2)" }}>
            CPF {pii?.cpf ?? p.cpf} · {p.age} anos{p.vehicle ? ` · ${p.vehicle.model} ${p.vehicle.year}` : ""}
          </span>
          <span className="muted" style={{ fontSize: 12 }}>Enviado {ago(p.submittedAt)}{p.reviewedBy ? ` · decidido por ${p.reviewedBy}` : ""}</span>
        </span>
        {p.vehicle && <Plate plate={p.vehicle.plate} />}
      </div>

      {p.liveness && <LivenessReview driver={p} canDecide={canDecide} onOpenDoc={(doc) => setViewing(doc.id)} onDone={done} />}

      {p.canRevealPii && !pii && (
        <button type="button" className="btn" style={{ alignSelf: "flex-start", background: "var(--soft)" }} onClick={async () => setPii(await reveal())}>
          <Icon name="eye" size={16} /> Ver dados completos <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· acesso registrado</span>
        </button>
      )}
      {pii && (
        <div className="facts">
          <div className="fact"><span>CPF</span><b className="mono">{pii.cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4")}</b></div>
          <div className="fact"><span>Telefone</span><b>{pii.phone?.replace(/^\+55(\d{2})(\d{4,5})(\d{4})$/, "+55 $1 $2-$3") ?? "—"}</b></div>
          <div className="fact"><span>CNH nº</span><b className="mono">{pii.cnhNumber}</b></div>
          <div className="fact"><span>Nascimento</span><b>{pii.birthDate.split("-").reverse().join("/")}</b></div>
          <div className="fact"><span>Endereço</span><b>{pii.address ?? "—"}</b></div>
          <div className="fact"><span>Chave Pix</span><b>{pii.pixKey ?? "—"}</b></div>
        </div>
      )}

      <div className="grid-auto" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))", gap: 8 }}>
        {p.checks.map((c) => (
          <div key={c.id} style={{ display: "flex", gap: 10, padding: 12, borderRadius: 14, background: "var(--soft)" }}>
            <span style={{ flex: "none", width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center", background: c.ok ? "var(--ok-dot)" : "#B7791F", color: "#fff" }}>
              <Icon name={c.ok ? "check" : "alert"} size={12} stroke={3.2} />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <b style={{ fontSize: 13 }}>{CHECKS[c.id] ?? c.id}</b>
              <span style={{ fontSize: 12, color: "var(--text-2)" }}>{c.detail}</span>
            </span>
          </div>
        ))}
      </div>

      {p.vehicle && (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          {p.vehicle.make} {p.vehicle.model} {p.vehicle.color.toLowerCase()} · tipos de corrida {p.vehicle.eligibleCategories.map((c) => typeLabel(c)).join(", ") || "—"}
          {p.pix ? ` · Pix ${p.pix.type.toUpperCase()} ${p.pix.key}${p.pix.verified ? " (verificada)" : ""}` : ""}
        </p>
      )}

      <DriverTripTypes driverId={driverId} tripTypes={p.tripTypes} gender={p.gender} hasVehicle={!!p.vehicle} canEdit={canEdit} onChanged={() => void qc.invalidateQueries({ queryKey: ["driver", driverId] })} />

      <div className="stack" style={{ gap: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          <b style={{ flex: "1 1 auto" }}>
            Documentos
            <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}> · {p.requiredKinds.length - toApprove.length} de {p.requiredKinds.length} aprovados{waiting.length ? ` · ${waiting.length} a analisar` : ""}</span>
          </b>
          {canDecide && waiting[0] && (
            <button type="button" className="btn" style={{ background: "var(--soft)" }} onClick={() => setViewing(waiting[0]!.id)}>
              <Icon name="eye" size={16} /> Analisar {waiting.length > 1 ? `os ${waiting.length} pendentes` : "o pendente"}
            </button>
          )}
        </div>
        <div className="grid-auto" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(140px, 100%), 1fr))", gap: 10 }}>
          {p.documents.length === 0 && <span className="muted">Nenhum documento enviado.</span>}
          {p.documents.map((doc) => {
            const meta = DOCS[doc.kind] ?? { label: doc.kind, icon: "doc" as const };
            const st = DOC_STATUS[doc.status] ?? DOC_STATUS.pending!;
            return (
              <button key={doc.id} type="button" onClick={() => setViewing(doc.id)} aria-label={`Abrir ${meta.label}`} style={{ display: "flex", flexDirection: "column", gap: 8, border: 0, padding: 0, background: "transparent", textAlign: "left" }}>
                <span className="hatch" style={{ width: "100%", height: 96, borderRadius: 12, display: "grid", placeItems: "center", color: "var(--muted-2)", outline: doc.status === "pending" ? "2px solid var(--warn)" : undefined, outlineOffset: -2 }}>
                  <Icon name={meta.icon} size={26} stroke={1.6} />
                </span>
                <span style={{ display: "flex", justifyContent: "space-between", gap: 6, width: "100%" }}>
                  <b style={{ fontSize: 13, minWidth: 0 }}>{meta.label}</b>
                  <span style={{ fontSize: 11, fontWeight: 700, color: st.tone, whiteSpace: "nowrap" }}>{st.label}</span>
                </span>
                {doc.rejectionReason && <span style={{ fontSize: 12, color: "var(--bad)" }}>{doc.rejectionReason}</span>}
                {doc.blocksGo && doc.status !== "approved" && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--bad)" }}>GO BLOQUEADO</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingTop: 6 }}>
        {!undecided || !canDecide ? (
          <span className={`pill ${REVIEW_STATUS[p.reviewStatus]?.tone ?? "tone-neutral"}`} style={{ height: 44, padding: "0 16px", borderRadius: 14, fontSize: 14 }}>
            {REVIEW_STATUS[p.reviewStatus]?.label ?? p.reviewStatus}
            {p.rejectionReason ? ` · ${p.rejectionReason}` : ""}
            {!canDecide && undecided ? " · sua função só visualiza" : ""}
          </span>
        ) : (
          <>
            {p.reviewStatus === "pending_review" && (toResend.length ? (
              <button type="button" className="btn btn-primary btn-lg" onClick={() => setDialog("resubmit")}>Pedir reenvio · {toResend.length} documento{toResend.length > 1 ? "s" : ""}</button>
            ) : (
              <button type="button" className="btn btn-primary btn-lg" disabled={toApprove.length > 0} onClick={() => setDialog("approve")}>Aprovar motorista</button>
            ))}
            <button type="button" className="btn btn-danger btn-lg" onClick={() => setDialog("reject")}>Reprovar</button>
            {p.reviewStatus === "pending_review" && (toResend.length > 0 || toApprove.length > 0) && (
              <span className="muted" style={{ alignSelf: "center", fontSize: 12 }}>
                {toResend.length
                  ? "Os documentos marcados para reenvio voltam ao motorista com os motivos."
                  : `Aprove cada documento para liberar o cadastro · falta${toApprove.length > 1 ? "m" : ""} ${toApprove.map((k) => docName(k)).join(", ")}`}
              </span>
            )}
          </>
        )}
        {canEdit && p.reviewStatus === "approved" && (
          <button type="button" className="btn btn-lg" onClick={() => setWelcoming(true)}><Icon name="send" size={16} />Reenviar boas-vindas</button>
        )}
      </div>

      {expiring && expiring.expiring.length > 0 && (
        <div className="notice" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          <span style={{ flex: "1 1 240px", display: "flex", flexDirection: "column", gap: 2 }}>
            <b>Documentos vencendo</b>
            <span>{expiring.expiring.map((e) => `${docName(e.kind)} · ${e.expiresAt < today() ? "venceu" : "vence"} ${e.expiresAt.split("-").reverse().join("/")}`).join(" · ")}</span>
            {expiring.renewalRequestedAt && <span className="muted" style={{ fontSize: 12 }}>Renovação pedida {ago(expiring.renewalRequestedAt)}</span>}
          </span>
          {canEdit && p.reviewStatus === "approved" && (
            <button type="button" className="btn btn-primary" onClick={() => setRenewing(true)}><Icon name="send" size={16} />{expiring.renewalRequestedAt ? "Pedir de novo" : "Pedir renovação"}</button>
          )}
        </div>
      )}
      {renewing && expiring && (
        <ConfirmDialog
          title="Pedir renovação de documentos"
          body={<>{p.fullName.split(" ")[0]} recebe uma notificação para enviar pelo app: {expiring.expiring.map((e) => docName(e.kind)).join(", ")}. Documentos com a data já vencida passam a constar como vencidos. Fica registrado na auditoria.</>}
          confirmLabel="Enviar pedido"
          onConfirm={async () => {
            await call(api.admin.drivers({ id: driverId })["request-renewal"].post());
            done("Pedido de renovação enviado ao motorista");
          }}
          onClose={() => setRenewing(false)}
        />
      )}
      {welcoming && <ResendWelcomeDialog driverId={driverId} who={p.fullName.split(" ")[0]!} onSent={() => done("E-mail de boas-vindas enviado")} onClose={() => setWelcoming(false)} />}
      {dialog && <DecisionDialog kind={dialog} driver={p} onClose={() => setDialog(null)} onDone={done} />}
      {viewing && (
        <DocumentViewer
          key={viewing}
          driver={p}
          documentId={viewing}
          canDecide={canDecide}
          onClose={() => setViewing(null)}
          onDecided={(msg, next) => {
            toast.show(msg);
            refresh();
            setViewing(next);
          }}
        />
      )}
      {toast.node}
    </div>
  );
}

type DriverView = Awaited<ReturnType<typeof getDriver>>;

/** What staff write most when asking for a document again (the driver reads it). */
const DOC_REASONS = ["Foto desfocada ou ilegível", "Documento cortado: envie a página inteira", "Documento vencido", "Não é o documento pedido", "Os dados não batem com o cadastro"];

/**
 * A document full size, with its decision: approve it, or ask for it again at any time (approved
 * drivers: optionally locking GO until a new one is approved). Approving moves on to the next one waiting.
 */
function DocumentViewer({ driver, documentId, canDecide, onClose, onDecided }: {
  driver: DriverView; documentId: string; canDecide: boolean; onClose: () => void; onDecided: (msg: string, next: string | null) => void;
}) {
  const doc = driver.documents.find((x) => x.id === documentId);
  const label = DOCS[doc?.kind ?? ""]?.label ?? doc?.kind ?? "Documento";
  const src = useQuery({
    queryKey: ["document-url", documentId],
    queryFn: () => call(api.admin.drivers({ id: driver.userId }).documents({ documentId }).url.get()),
    gcTime: 0,
    staleTime: 0,
  });
  const live = driver.reviewStatus === "approved";
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [blocksGo, setBlocksGo] = useState(doc?.status === "expired" || (!!doc?.expiresAt && doc.expiresAt < today()));
  const decide = useMutation({
    mutationFn: (decision: "approve" | "resend") =>
      call(api.admin.drivers({ id: driver.userId }).documents({ documentId }).decision.post(
        decision === "approve" ? { decision } : { decision, reason: reason.trim(), ...(live ? { blocksGo } : {}) },
      )),
    onSuccess: (r) => {
      const next = driver.documents.find((x) => x.id !== documentId && x.status === "pending");
      if (r.status === "approved") onDecided(`${label} aprovado${r.notified ? " · motorista avisado" : ""}`, next?.id ?? null);
      else onDecided(`Reenvio de ${label} pedido${r.notified ? " · motorista avisado" : ""}${r.blocksGo ? " · GO bloqueado" : ""}`, null);
    },
  });
  const st = DOC_STATUS[doc?.status ?? "pending"] ?? DOC_STATUS.pending!;
  const first = driver.fullName.split(" ")[0];

  return (
    <Dialog title={label} wide onClose={onClose}>
      {src.data ? (
        src.data.isPdf
          ? <iframe src={src.data.url} title={label} style={{ width: "100%", height: "62vh", border: 0, borderRadius: 14, background: "var(--soft)" }} />
          : <img src={src.data.url} alt={label} style={{ width: "100%", maxHeight: "62vh", objectFit: "contain", borderRadius: 14, background: "var(--soft)" }} />
      ) : src.isPending ? <Skeleton h={320} /> : null}
      <ErrorBox error={src.error} />

      {doc && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          <span style={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13 }}>
              <b style={{ color: st.tone, fontSize: 11 }}>{st.label}</b> · enviado {ago(doc.uploadedAt)}
              {doc.status === "rejected" && doc.reviewedAt ? ` · reenvio pedido ${ago(doc.reviewedAt)}` : ""}
              {doc.blocksGo && doc.status !== "approved" ? " · GO bloqueado" : ""}
            </span>
            {doc.rejectionReason && <span style={{ fontSize: 12, color: "var(--bad)" }}>{doc.rejectionReason}</span>}
            <span className="muted" style={{ fontSize: 12 }}>Link temporário (2 min). Este acesso foi registrado na auditoria.</span>
          </span>
          {canDecide && !asking && (
            <span style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {doc.status !== "rejected" && <button type="button" className="btn" style={{ background: "var(--soft)" }} onClick={() => setAsking(true)}>Pedir reenvio</button>}
              {(doc.status === "pending" || doc.status === "rejected") && (
                <button type="button" className="btn btn-primary" disabled={decide.isPending} onClick={() => decide.mutate("approve")}>
                  <Icon name="check" size={16} stroke={2.6} /> Aprovar documento
                </button>
              )}
            </span>
          )}
        </div>
      )}

      {doc && asking && (
        <form className="stack" style={{ gap: 12, paddingTop: 4, borderTop: "1px solid var(--line)" }} onSubmit={(e) => { e.preventDefault(); decide.mutate("resend"); }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {DOC_REASONS.map((r) => (
              <button key={r} type="button" className="btn" aria-pressed={reason === r} style={{ height: 32, fontSize: 13, background: reason === r ? "var(--chip)" : "var(--soft)" }} onClick={() => setReason(r)}>{r}</button>
            ))}
          </div>
          <label className="field">Motivo (o motorista verá esta mensagem)
            <textarea className="textarea" required minLength={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: a foto da CNH está desfocada" />
          </label>
          {live ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 14, background: "var(--soft)" }}>
              <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <b style={{ fontSize: 13 }}>Bloquear GO até aprovar</b>
                <span className="muted" style={{ fontSize: 12 }}>
                  {blocksGo ? `${first} fica offline agora e só volta depois que você aprovar o novo documento.` : `${first} continua recebendo corridas enquanto envia o novo documento.`}
                </span>
              </span>
              <Switch checked={blocksGo} onChange={setBlocksGo} label="Bloquear GO até aprovar" />
            </div>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>
              {driver.reviewStatus === "pending_review" ? `${first} recebe o pedido quando você concluir a análise com "Pedir reenvio".` : `${first} vê o pedido na próxima vez que abrir o cadastro.`}
            </span>
          )}
          <ErrorBox error={decide.error} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button type="button" className="btn" style={{ background: "var(--soft)" }} onClick={() => setAsking(false)}>Voltar</button>
            <button type="submit" className={`btn ${blocksGo && live ? "btn-danger" : "btn-primary"}`} disabled={decide.isPending || reason.trim().length < 3}>
              {live ? (blocksGo ? "Pedir reenvio e bloquear GO" : "Pedir reenvio") : "Marcar para reenvio"}
            </button>
          </div>
        </form>
      )}
      {!asking && <ErrorBox error={decide.error} />}
    </Dialog>
  );
}

/** Prova de vida: the reasons staff pick most when asking for a new video (the driver reads them). */
const LIVENESS_REASONS = ["O rosto ficou coberto (boné, óculos ou máscara)", "Pouca luz: o rosto não aparece bem", "Não virou o rosto para os dois lados", "O vídeo ficou cortado"];
const fmtSeconds = (ms: number) => `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
const clockOf = (ms: number) => `0:${String(Math.round(ms / 1000)).padStart(2, "0")}`;

/** Cadastros → Prova de vida: the video next to the CNH and profile photo, what the app measured, and the decision. */
function LivenessReview({ driver, canDecide, onOpenDoc, onDone }: {
  driver: DriverView; canDecide: boolean; onOpenDoc: (doc: { id: string; kind: string }) => void; onDone: (msg: string) => void;
}) {
  const live = driver.liveness!;
  const [dialog, setDialog] = useState<null | "approve" | "retry" | "suspend">(null);
  /** The player modal, opened at this many ms (a step's chip) or from the start. */
  const [playingAt, setPlayingAt] = useState<number | null>(null);
  const cnhFront = driver.documents.find((d) => d.kind === "cnh_front");
  const m = live.meta;
  const st = DOC_STATUS[live.status] ?? DOC_STATUS.pending!;
  const decidable = canDecide && live.status === "pending" && driver.reviewStatus === "approved";
  const askAgain = canDecide && live.status === "approved" && driver.reviewStatus === "approved";
  const signals = m
    ? [
      { label: "Um rosto, o tempo todo", value: m.multipleFaceFrames === 0 ? fmtSeconds(m.durationMs) : `${m.multipleFaceFrames} quadros com outro rosto`, ok: m.multipleFaceFrames === 0 },
      { label: "Olhou para a câmera", value: clockOf(m.stepsMs.front), ok: true },
      { label: "Virou para a esquerda", value: `${Math.round(m.peakYawLeft)}°`, ok: m.peakYawLeft >= 20 },
      { label: "Virou para a direita", value: `${Math.round(m.peakYawRight)}°`, ok: m.peakYawRight >= 20 },
    ]
    : [];

  return (
    <section aria-label="Prova de vida" className="stack" style={{ gap: 14, padding: 16, borderRadius: 18, border: "1px solid var(--line)" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
        <b className="display" style={{ fontSize: 16, flex: "1 1 auto" }}>Prova de vida</b>
        <span className="muted" style={{ fontSize: 12 }}>{live.attempt}ª tentativa · enviado {ago(live.uploadedAt)}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: st.tone }}>{live.status === "rejected" ? "NOVO VÍDEO PEDIDO" : st.label}</span>
      </div>
      {live.rejectionReason && <span style={{ fontSize: 13, color: "var(--bad)" }}>{live.rejectionReason}</span>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
        <div style={{ flex: "0 1 240px", display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={() => setPlayingAt(0)}
            disabled={!live.hasVideo}
            aria-label="Assistir ao vídeo"
            style={{ position: "relative", width: "100%", maxWidth: 240, aspectRatio: "9 / 16", borderRadius: 16, border: 0, padding: 0, overflow: "hidden", background: "#15110E", color: "#FAF8F6", display: "grid", placeItems: "center", cursor: live.hasVideo ? "pointer" : "default" }}
          >
            <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <span style={{ width: 56, height: 56, borderRadius: 28, background: "rgba(250,248,246,0.92)", color: "#15110E", display: "grid", placeItems: "center" }}>
                <Icon name="play" size={20} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{live.hasVideo ? `Assistir · ${m ? fmtSeconds(m.durationMs) : "vídeo"}` : "Sem vídeo (aprovado em dev)"}</span>
            </span>
          </button>
          {m && live.hasVideo && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {steps(m).map(([label, ms]) => (
                <button key={label} type="button" className="btn" style={{ height: 30, fontSize: 12, background: "var(--soft)" }} onClick={() => setPlayingAt(ms)}>
                  {label} {clockOf(ms)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="stack" style={{ gap: 8 }}>
            <span className="field">Compare com</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              <button type="button" disabled={!cnhFront} onClick={() => cnhFront && onOpenDoc(cnhFront)} style={{ border: 0, padding: 0, background: "transparent", textAlign: "left", display: "flex", flexDirection: "column", gap: 6 }}>
                <span className="hatch" style={{ aspectRatio: "3 / 4", borderRadius: 12, display: "grid", placeItems: "center", color: "var(--muted-2)" }}><Icon name="idcard" size={26} stroke={1.6} /></span>
                <b style={{ fontSize: 12 }}>CNH · frente{cnhFront ? "" : " (não enviada)"}</b>
              </button>
              <span style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {driver.photoUrl
                  ? <img src={driver.photoUrl} alt="Foto de perfil" style={{ aspectRatio: "3 / 4", width: "100%", objectFit: "cover", borderRadius: 12, background: "var(--soft)" }} />
                  : <span className="hatch" style={{ aspectRatio: "3 / 4", borderRadius: 12, display: "grid", placeItems: "center", color: "var(--muted-2)" }}><Icon name="camera" size={26} stroke={1.6} /></span>}
                <b style={{ fontSize: 12 }}>Foto de perfil</b>
              </span>
            </div>
          </div>
          {m && (
            <div className="stack" style={{ gap: 8 }}>
              <span className="field">Sinais do app na gravação</span>
              <div style={{ borderRadius: 14, border: "1px solid var(--line)" }}>
                {signals.map((s, i) => (
                  <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: i ? "1px solid var(--line)" : 0 }}>
                    <span style={{ color: s.ok ? "var(--ok)" : "var(--warn)", display: "grid" }}><Icon name={s.ok ? "check" : "alert"} size={15} stroke={2.6} /></span>
                    <span style={{ flex: 1 }}>{s.label}</span>
                    <span className="muted num" style={{ fontSize: 12 }}>{s.value}</span>
                  </div>
                ))}
              </div>
              <span className="muted" style={{ fontSize: 12 }}>Gravado ao vivo no app · {m.device} · {m.platform === "ios" ? "iOS" : "Android"} · não substitui a sua conferência</span>
            </div>
          )}
        </div>
      </div>

      {decidable && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button type="button" className="btn btn-primary btn-lg" onClick={() => setDialog("approve")}>Confirmar identidade e liberar GO</button>
          <button type="button" className="btn btn-lg" style={{ background: "var(--soft)" }} onClick={() => setDialog("retry")}>Pedir novo vídeo</button>
          <button type="button" className="btn btn-danger btn-lg" onClick={() => setDialog("suspend")}>Não é a mesma pessoa</button>
        </div>
      )}
      {askAgain && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          <button type="button" className="btn" style={{ background: "var(--soft)" }} onClick={() => setDialog("retry")}>Pedir novo vídeo</button>
          <span className="muted" style={{ fontSize: 12 }}>O GO fica bloqueado até você aprovar o novo vídeo.</span>
        </div>
      )}
      {dialog && <LivenessDialog kind={dialog} driver={driver} onClose={() => setDialog(null)} onDone={onDone} />}
      {playingAt !== null && <LivenessPlayer driver={driver} startMs={playingAt} onClose={() => setPlayingAt(null)} />}
    </section>
  );
}

type LivenessMetaView = NonNullable<NonNullable<DriverView["liveness"]>["meta"]>;
/** The three steps and when each finished. */
const steps = (m: LivenessMetaView) => [["Frente", m.stepsMs.front], ["Esquerda", m.stepsMs.left], ["Direita", m.stepsMs.right]] as const;

/**
 * The prova de vida video in a modal, big enough to compare with the CNH: a fresh signed link per
 * opening (each viewing is audited), the step chips seek a beat before each turn finished.
 */
function LivenessPlayer({ driver, startMs, onClose }: { driver: DriverView; startMs: number; onClose: () => void }) {
  const live = driver.liveness!;
  const video = useRef<HTMLVideoElement>(null);
  const src = useQuery({
    queryKey: ["liveness-url", live.id],
    queryFn: () => call(api.admin.drivers({ id: driver.userId }).documents({ documentId: live.id }).url.get()),
    gcTime: 0,
    staleTime: 0,
  });
  const seek = (ms: number) => {
    const v = video.current;
    if (!v) return;
    v.currentTime = Math.max(0, ms - 1200) / 1000;
    void v.play().catch(() => {});
  };
  return (
    <Dialog title={`Prova de vida · ${driver.fullName}`} wide onClose={onClose}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start", justifyContent: "center" }}>
        <div style={{ height: "min(68vh, 640px)", aspectRatio: "9 / 16", maxWidth: "100%", borderRadius: 16, overflow: "hidden", background: "#15110E", display: "grid", placeItems: "center" }}>
          {src.data ? (
            <video
              ref={video}
              src={src.data.url}
              controls
              autoPlay
              playsInline
              muted
              onLoadedMetadata={() => startMs > 0 && seek(startMs)}
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#15110E" }}
            />
          ) : src.isPending ? <Skeleton h={120} /> : null}
        </div>
        <div className="stack" style={{ flex: "1 1 220px", minWidth: 0, gap: 12 }}>
          {live.meta && (
            <div className="stack" style={{ gap: 6 }}>
              <span className="field">Ir para</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {steps(live.meta).map(([label, ms]) => (
                  <button key={label} type="button" className="btn" style={{ height: 32, fontSize: 13, background: "var(--soft)" }} disabled={!src.data} onClick={() => seek(ms)}>
                    {label} {clockOf(ms)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {driver.photoUrl && (
            <div className="stack" style={{ gap: 6 }}>
              <span className="field">Foto de perfil</span>
              <img src={driver.photoUrl} alt="Foto de perfil" style={{ width: "100%", maxWidth: 220, aspectRatio: "3 / 4", objectFit: "cover", borderRadius: 12, background: "var(--soft)" }} />
            </div>
          )}
          <span className="muted" style={{ fontSize: 12 }}>Link temporário (2 min). Este acesso foi registrado na auditoria.</span>
          <ErrorBox error={src.error} />
        </div>
      </div>
    </Dialog>
  );
}

function LivenessDialog({ kind, driver, onClose, onDone }: { kind: "approve" | "retry" | "suspend"; driver: DriverView; onClose: () => void; onDone: (msg: string) => void }) {
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () => call(api.admin.drivers({ id: driver.userId }).liveness.post({ decision: kind, ...(kind !== "approve" ? { reason: reason.trim() } : {}) })),
    onSuccess: () => onDone({ approve: "Identidade confirmada · motorista já pode ficar online", retry: "Novo vídeo pedido · motorista avisado", suspend: "Conta suspensa · motorista avisado" }[kind]),
  });
  const title = { approve: "Confirmar identidade", retry: "Pedir novo vídeo", suspend: "Suspender a conta" }[kind];
  const first = driver.fullName.split(" ")[0];
  return (
    <Dialog title={title} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {kind === "approve" && <p style={{ margin: 0 }}>O rosto do vídeo é o mesmo da CNH de {driver.fullName}. {first} recebe uma notificação e o GO é liberado.</p>}
        {kind === "retry" && driver.liveness?.status === "approved" && <p style={{ margin: 0 }}>{first} fica offline agora e grava um novo vídeo no próximo GO. O GO volta quando você aprovar.</p>}
        {kind === "suspend" && <p style={{ margin: 0 }}>Use quando a pessoa do vídeo não é a da CNH. A conta fica suspensa e {first} não consegue ficar online até a equipe reativar.</p>}
        {kind === "retry" && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {LIVENESS_REASONS.map((r) => (
              <button key={r} type="button" className="btn" aria-pressed={reason === r} style={{ height: 32, fontSize: 13, background: reason === r ? "var(--chip)" : "var(--soft)" }} onClick={() => setReason(r)}>{r}</button>
            ))}
          </div>
        )}
        {kind !== "approve" && (
          <label className="field">Motivo (o motorista verá esta mensagem)
            <textarea className="textarea" required minLength={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={kind === "retry" ? "Ex.: o boné cobriu parte do rosto" : "Ex.: o rosto não é o da CNH"} />
          </label>
        )}
        <ErrorBox error={m.error} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" className="btn" style={{ background: "var(--soft)" }} onClick={onClose}>Cancelar</button>
          <button type="submit" className={`btn ${kind === "suspend" ? "btn-danger" : "btn-primary"}`} disabled={m.isPending || (kind !== "approve" && reason.trim().length < 3)}>{title}</button>
        </div>
      </form>
    </Dialog>
  );
}
const getDriver = (id: string) => call(api.admin.drivers({ id }).get());

/** How the welcome email went, after "Aprovado · motorista avisado no app". */
const WELCOME_EMAIL = { sent: " · e-mail de boas-vindas enviado", no_email: " · sem e-mail cadastrado", failed: " · e-mail não enviado, use Reenviar boas-vindas" };

function DecisionDialog({ kind, driver, onClose, onDone }: { kind: "approve" | "resubmit" | "reject"; driver: DriverView; onClose: () => void; onDone: (msg: string) => void }) {
  const [reason, setReason] = useState("");
  const resend = driver.documents.filter((d) => d.status === "rejected" || d.status === "expired");
  const m = useMutation({
    mutationFn: () =>
      call(api.admin.drivers({ id: driver.userId }).review.post({
        decision: kind,
        ...(kind !== "approve" && reason.trim() ? { reason: reason.trim() } : {}),
      })),
    onSuccess: (r) =>
      onDone(kind === "approve" ? `Aprovado · motorista avisado no app${WELCOME_EMAIL[r.welcomeEmail ?? "sent"]}` : kind === "resubmit" ? "Reenvio pedido · motorista avisado" : "Reprovado · motivo enviado"),
  });
  const title = { approve: "Aprovar motorista", resubmit: "Pedir reenvio", reject: "Reprovar cadastro" }[kind];
  return (
    <Dialog title={title} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {kind === "approve" && <p style={{ margin: 0 }}>Todos os documentos foram aprovados. {driver.fullName} poderá ficar online em {driver.vehicle ? typeLabel(driver.vehicle.eligibleCategories[0] ?? "go") : "Go"} assim que você confirmar.</p>}
        {kind === "resubmit" && (
          <div className="stack" style={{ gap: 6 }}>
            <span className="field">Voltam ao motorista</span>
            {resend.map((d) => (
              <span key={d.id} style={{ display: "flex", flexDirection: "column", gap: 1, padding: "8px 12px", borderRadius: 12, background: "var(--soft)" }}>
                <b style={{ fontSize: 13 }}>{DOCS[d.kind]?.label ?? d.kind}</b>
                {d.rejectionReason && <span style={{ fontSize: 12, color: "var(--text-2)" }}>{d.rejectionReason}</span>}
              </span>
            ))}
          </div>
        )}
        {kind !== "approve" && (
          <label className="field">{kind === "resubmit" ? "Mensagem geral (opcional)" : "Motivo (o motorista verá esta mensagem)"}
            <textarea className="textarea" required={kind === "reject"} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={kind === "resubmit" ? "Ex.: falta pouco! Envie os documentos abaixo de novo." : "Ex.: antecedentes incompatíveis com a atividade"} />
          </label>
        )}
        <ErrorBox error={m.error} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button type="button" className="btn" style={{ background: "var(--soft)" }} onClick={onClose}>Cancelar</button>
          <button type="submit" className={`btn ${kind === "reject" ? "btn-danger" : "btn-primary"}`} disabled={m.isPending || (kind === "reject" && !reason.trim())}>{title}</button>
        </div>
      </form>
    </Dialog>
  );
}

function Plate({ plate }: { plate: string }) {
  return (
    <div aria-label={`Placa ${plate}`} style={{ width: 136, borderRadius: 8, overflow: "hidden", border: "2px solid #15110E", background: "#fff", color: "#15110E", display: "flex", flexDirection: "column" }}>
      <span style={{ height: 12, background: "#1C3F94", color: "#fff", fontSize: 7, fontWeight: 700, letterSpacing: ".2em", display: "grid", placeItems: "center" }}>BRASIL</span>
      <span className="display" style={{ height: 34, display: "grid", placeItems: "center", fontWeight: 700, fontSize: 21, letterSpacing: ".05em" }}>{plate}</span>
    </div>
  );
}

const GENDER_LABEL = { female: "Mulher", male: "Homem" } as const;

/**
 * Tipos de corrida for this driver: what the rules allow, the staff grant for approval-only types
 * (Black), and the declared gender, which staff confirm against the CNH before gender rules use it.
 */
function DriverTripTypes({ driverId, tripTypes, gender, hasVehicle, canEdit, onChanged }: {
  driverId: string; tripTypes: DriverView["tripTypes"]; gender: DriverView["gender"]; hasVehicle: boolean; canEdit: boolean; onChanged: () => void;
}) {
  const grant = useMutation({
    mutationFn: (granted: string[]) => call(api.admin.drivers({ id: driverId }).vehicle["trip-types"].put({ granted })),
    onSuccess: onChanged,
  });
  const setGender = useMutation({
    mutationFn: (body: { gender: "female" | "male" | null; confirmed: boolean }) => call(api.admin.drivers({ id: driverId }).gender.put(body)),
    onSuccess: onChanged,
  });
  const granted = tripTypes.filter((t) => t.granted).map((t) => t.id);
  return (
    <section className="card pad stack" style={{ gap: 10 }}>
      <h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="tick" />Tipos de corrida</h2>
      <div className="setting-row" style={{ paddingTop: 0 }}>
        <span className="setting-text">
          <b>Gênero{gender.value ? `: ${GENDER_LABEL[gender.value]}` : ": não informado"}</b>
          <span>{gender.value ? (gender.confirmedAt ? "Confirmado com a CNH: vale para tipos exclusivos." : "Declarado pelo motorista. Confira na CNH antes de confirmar.") : "Só é usado em tipos exclusivos para um gênero."}</span>
        </span>
        {canEdit && (
          <span className="setting-control" style={{ flexBasis: "auto" }}>
            <select className="select" style={{ width: 130 }} aria-label="Gênero" value={gender.value ?? ""} disabled={setGender.isPending}
              onChange={(e) => setGender.mutate({ gender: (e.target.value || null) as "female" | "male" | null, confirmed: false })}>
              <option value="">Não informado</option>
              <option value="female">Mulher</option>
              <option value="male">Homem</option>
            </select>
            {gender.value && (
              <button type="button" className="btn" disabled={setGender.isPending} onClick={() => setGender.mutate({ gender: gender.value, confirmed: !gender.confirmedAt })}>
                {gender.confirmedAt ? "Desfazer confirmação" : "Confirmar com a CNH"}
              </button>
            )}
          </span>
        )}
      </div>
      <ErrorBox error={setGender.error ?? grant.error} />
      {tripTypes.map((t) => (
        <div key={t.id} className="setting-row">
          <span className="setting-text">
            <b style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{t.name}{t.service === "delivery" && <span className="tag">Envio</span>}</b>
            <span>{t.eligible ? (t.requiresApproval ? "Liberado pela equipe" : "Pode receber") : t.reason}</span>
          </span>
          <span className="setting-control" style={{ flexBasis: "auto" }}>
            <span className={`pill ${t.eligible ? "tone-ok" : "tone-neutral"}`}>{t.eligible ? "Elegível" : "Não elegível"}</span>
            {t.requiresApproval && (
              <Switch
                checked={t.granted}
                disabled={!canEdit || !hasVehicle || grant.isPending}
                label={`Liberar ${t.name} para este veículo`}
                onChange={(on) => grant.mutate(on ? [...granted, t.id] : granted.filter((x) => x !== t.id))}
              />
            )}
          </span>
        </div>
      ))}
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>Quem já está online passa a receber as novas opções na hora; o motorista ainda escolhe os tipos opcionais na Conta.</p>
    </section>
  );
}

